import React, { useState, useMemo } from 'react';
import { useTask } from '../contexts/TaskContext';
import { useAuth } from '../../../hooks/useAuth';
// SUPABASE SWITCH: revert to '../services/firestore' for Firebase
import { createTask, updateTask } from '../services/firestore';
import { Plus, ChevronLeft, ChevronRight, Clock, Repeat } from 'lucide-react';
import toast from 'react-hot-toast';

const DAY_NAMES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const DAY_SHORTS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const priorityColors = {
  alta: 'border-l-red-500 bg-red-50 dark:bg-red-900/10',
  media: 'border-l-amber-500 bg-amber-50 dark:bg-amber-900/10',
  baja: 'border-l-green-500 bg-green-50 dark:bg-green-900/10',
};

const statusDot = {
  planificacion: 'bg-gray-400',
  'en-curso': 'bg-blue-500',
  finalizado: 'bg-green-500',
};

const WeeklyView = () => {
  const { user } = useAuth();
  const {
    filteredTasks: tasks = [],
    selectedProjectId,
    selectedFolderId,
    selectedListId,
    setSelectedTaskId,
    setShowTaskDrawer,
  } = useTask();

  const [weekOffset, setWeekOffset] = useState(0);
  const [addingDay, setAddingDay] = useState(null);
  const [newTitle, setNewTitle] = useState('');

  // Get Monday of current week
  const weekStart = useMemo(() => {
    const now = new Date();
    const day = now.getDay();
    const diff = day === 0 ? -6 : 1 - day; // Monday
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff + weekOffset * 7);
    monday.setHours(0, 0, 0, 0);
    return monday;
  }, [weekOffset]);

  // Generate 6 days (Mon-Sat)
  const weekDays = useMemo(() => {
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isToday = (date) => date.getTime() === today.getTime();

  const isSameDay = (d1, d2) => {
    return d1.getDate() === d2.getDate() && d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();
  };

  const parseDate = (d) => {
    if (!d) return null;
    if (typeof d === 'object' && d.seconds) return new Date(d.seconds * 1000);
    if (typeof d === 'string') return new Date(d);
    return null;
  };

  // Group tasks by day of week
  const tasksByDay = useMemo(() => {
    const grouped = {};
    weekDays.forEach(day => {
      grouped[day.toISOString()] = [];
    });

    tasks.forEach(task => {
      const dueDate = parseDate(task.fechaVencimiento);
      if (!dueDate) return;

      // Check if task falls on any day this week
      weekDays.forEach(day => {
        if (isSameDay(dueDate, day)) {
          grouped[day.toISOString()].push(task);
        }
      });

      // Recurring tasks: show on matching days
      if (task.recurrence && task.recurrence !== 'none') {
        const dayOfWeek = dueDate.getDay();
        weekDays.forEach(day => {
          if (isSameDay(dueDate, day)) return; // Already added
          const shouldShow = shouldRecurOnDay(task, day, dueDate);
          if (shouldShow) {
            grouped[day.toISOString()].push({ ...task, _isRecurrence: true });
          }
        });
      }
    });

    return grouped;
  }, [tasks, weekDays]);

  const shouldRecurOnDay = (task, targetDay, originalDate) => {
    if (targetDay < originalDate) return false;
    const rec = task.recurrence;
    if (rec === 'daily') return true;
    if (rec === 'weekly') return targetDay.getDay() === originalDate.getDay();
    if (rec === 'biweekly') {
      const diff = Math.floor((targetDay - originalDate) / (1000 * 60 * 60 * 24));
      return diff % 14 === 0;
    }
    if (rec === 'monthly') return targetDay.getDate() === originalDate.getDate();
    return false;
  };

  const handleAddTask = async (dayDate) => {
    if (!newTitle.trim() || !user?.uid || !selectedProjectId || !selectedFolderId || !selectedListId) return;
    try {
      await createTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, {
        titulo: newTitle.trim(),
        fechaVencimiento: dayDate.toISOString(),
      });
      setNewTitle('');
      setAddingDay(null);
      toast.success('Tarea creada');
    } catch {
      toast.error('Error al crear');
    }
  };

  const handleTaskClick = (taskId) => {
    setSelectedTaskId(taskId);
    setShowTaskDrawer(true);
  };

  const handleToggleComplete = async (e, task) => {
    e.stopPropagation();
    const newStatus = task.estado === 'finalizado' ? 'en-curso' : 'finalizado';
    try {
      await updateTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, task.id, { estado: newStatus });
    } catch {}
  };

  const weekLabel = useMemo(() => {
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 5);
    const opts = { month: 'short', day: 'numeric' };
    return `${weekStart.toLocaleDateString('es-ES', opts)} - ${end.toLocaleDateString('es-ES', opts)}, ${end.getFullYear()}`;
  }, [weekStart]);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 shrink-0">
        <div className="flex items-center gap-3">
          <Clock size={16} className="text-blue-500" />
          <span className="text-sm font-bold text-gray-800 dark:text-gray-100">Vista Semanal</span>
          <span className="text-xs text-gray-500 font-medium">{weekLabel}</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setWeekOffset(0)} className="px-2 py-1 text-[10px] font-medium text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded">
            Hoy
          </button>
          <button onClick={() => setWeekOffset(w => w - 1)} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500">
            <ChevronLeft size={16} />
          </button>
          <button onClick={() => setWeekOffset(w => w + 1)} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500">
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Weekly Grid */}
      <div className="flex-1 overflow-auto">
        <div className="grid grid-cols-6 min-h-full divide-x divide-gray-200 dark:divide-gray-700">
          {weekDays.map((day, idx) => {
            const dayKey = day.toISOString();
            const dayTasks = tasksByDay[dayKey] || [];
            const isCurrentDay = isToday(day);
            const isPast = day < today;

            return (
              <div key={idx} className={`flex flex-col min-h-[400px] ${isCurrentDay ? 'bg-blue-50/30 dark:bg-blue-900/5' : ''}`}>
                {/* Day Header */}
                <div className={`sticky top-0 z-10 px-3 py-2.5 border-b border-gray-200 dark:border-gray-700 text-center ${isCurrentDay ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-gray-900'}`}>
                  <p className={`text-[10px] font-bold uppercase tracking-wider ${isCurrentDay ? 'text-blue-600' : 'text-gray-500'}`}>
                    {DAY_SHORTS[idx]}
                  </p>
                  <p className={`text-lg font-bold mt-0.5 ${isCurrentDay ? 'text-blue-600 bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center mx-auto' : isPast ? 'text-gray-400' : 'text-gray-800 dark:text-gray-200'}`}>
                    {day.getDate()}
                  </p>
                  <p className="text-[9px] text-gray-400 mt-0.5">{dayTasks.length} tarea{dayTasks.length !== 1 ? 's' : ''}</p>
                </div>

                {/* Tasks */}
                <div className="flex-1 p-2 space-y-1.5">
                  {dayTasks.map((task, tIdx) => (
                    <div
                      key={`${task.id}-${tIdx}`}
                      onClick={() => handleTaskClick(task.id)}
                      className={`p-2 rounded-lg border-l-[3px] cursor-pointer transition-all hover:shadow-sm group ${
                        priorityColors[task.prioridad] || 'border-l-gray-400 bg-gray-50 dark:bg-gray-800'
                      } ${task.estado === 'finalizado' ? 'opacity-50' : ''}`}
                    >
                      <div className="flex items-start gap-1.5">
                        <button
                          onClick={(e) => handleToggleComplete(e, task)}
                          className="mt-0.5 shrink-0"
                        >
                          <div className={`w-3.5 h-3.5 rounded-full border-2 ${
                            task.estado === 'finalizado'
                              ? 'bg-green-500 border-green-500'
                              : 'border-gray-300 group-hover:border-gray-400'
                          }`} />
                        </button>
                        <span className={`text-[11px] leading-tight font-medium ${
                          task.estado === 'finalizado' ? 'line-through text-gray-400' : 'text-gray-800 dark:text-gray-200'
                        }`}>
                          {task.titulo}
                        </span>
                      </div>
                      {task._isRecurrence && (
                        <div className="flex items-center gap-1 mt-1 ml-5">
                          <Repeat size={9} className="text-purple-500" />
                          <span className="text-[9px] text-purple-500 font-medium">Recurrente</span>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Add task */}
                  {addingDay === idx ? (
                    <div className="p-1.5">
                      <input
                        type="text"
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleAddTask(day);
                          if (e.key === 'Escape') { setAddingDay(null); setNewTitle(''); }
                        }}
                        placeholder="Nueva tarea..."
                        className="w-full px-2 py-1 text-[11px] border border-blue-300 rounded bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none"
                        autoFocus
                      />
                      <div className="flex gap-1 mt-1">
                        <button onClick={() => handleAddTask(day)} className="px-2 py-0.5 bg-blue-600 text-white rounded text-[10px] font-medium">Crear</button>
                        <button onClick={() => { setAddingDay(null); setNewTitle(''); }} className="text-[10px] text-gray-500">Cancelar</button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setAddingDay(idx); setNewTitle(''); }}
                      className="w-full py-1.5 flex items-center justify-center gap-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded text-[10px] transition-colors"
                    >
                      <Plus size={12} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WeeklyView;
